import uuid from "react-uuid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import HomeIcon from "@material-ui/icons/Home";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import SettingsIcon from "@material-ui/icons/Settings";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import Share from "@material-ui/icons/Share";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import { setMenuActivated } from "../../modules/actions";
import { assetClasses } from "../../utils/dropdownValues";
import menuStyles from "./menu.less";
import { useLocation } from "react-router";

let menuNodes = [];
let assetIds; // Use to compare asset list is changed
let menuActivated = null;
let relativeMenus = [];

function isAssetsChanged(assets) {
  const newAssetIds = [];
  assets.forEach((asset) => {
    newAssetIds.push(asset.id);
    if (asset.public_instruments) {
      asset.public_instruments.forEach((instrument) =>
        newAssetIds.push(instrument.security_id)
      );
    }
  });
  if (JSON.stringify(newAssetIds) === JSON.stringify(assetIds)) {
    return false;
  }
  assetIds = newAssetIds;
  return true;
}

export function createMenuNode(menu, parent) {
  menu.id = menu.id || uuid();
  menu.level = menu.level || 0;
  if (parent) {
    parent.level = parent.level || 0;
    menu.parentId = parent.id;
    menu.level = parent.level + 1;
  }
  // Clone exist menu state
  const oldMenu = menuNodes.find((e) => e.id === menu.id);
  if (oldMenu) {
    menu.expanded = oldMenu.expanded;
    menu.activated = oldMenu.activated;
    menuNodes.splice(menuNodes.indexOf(oldMenu), 1);
  }
  menuNodes.push(menu);
  return menu;
}

function assetToMenuNode(asset, parent) {
  const isAssetGroup = asset.public_instruments && asset.is_public_instrument;
  const menuNode = createMenuNode(
    {
      name: asset.name,
      path: isAssetGroup ? `/pi/${asset.id}` : `/asset/${asset.id}`,
      id: asset.id,
      asset: asset,
    },
    parent
  );
  if (asset.public_instruments) {
    menuNode.children = asset.public_instruments.map((item) => {
      if (item && item.is_cash)
        return publicInstrumentCashToMenuNode(item, menuNode);
      else return publicInstrumentToMenuNode(item, menuNode);
    });
  }
  return menuNode;
}

function publicInstrumentToMenuNode(asset, parent) {
  return createMenuNode(
    {
      name: asset.security_name,
      path: `/publicinstrument/${asset.security_id}`,
      id: asset.security_id,
      asset: asset,
    },
    parent
  );
}

function publicInstrumentCashToMenuNode(asset, parent) {
  return createMenuNode(
    {
      name: asset.security_name,
      path: `/publicinstrument/cash/${asset.security_id}`,
      id: asset.security_id,
      asset: asset,
    },
    parent
  );
}

function buildMenuTree(assetList) {
  if (!isAssetsChanged(assetList)) {
    return assetMenu;
  }
  const assetByClass = {
    investment: [],
    cash: [],
    loans: [],
  };

  assetList.forEach((asset) =>
    (assetByClass[asset.asset_class] || assetByClass.investment).push(asset)
  );
  updateInvestmentMenu(assetByClass.investment);
  updateCashMenu(assetByClass.cash);
  updateLoanMenu(assetByClass.loans);

  // Clone to force changes
  assetMenu = assetMenu.slice();

  // Activated menu item
  onMenuChange(window.location.pathname);

  return assetMenu;
}

function sortByAlphabet(items) {
  if (!items) {
    return;
  }
  items.sort((a, b) => a.name.localeCompare(b.name));

  // Sort children
  items.forEach((item) => sortByAlphabet(item.children));
}

function updateInvestmentMenu(investmentAssets) {
  const investmentByClass = _.groupBy(investmentAssets, "asset_class");
  const investmentClasses = Object.keys(investmentByClass).map((assetClass) => {
    const assetGroupName =
      assetClasses.find((e) => e.value === assetClass)?.label ||
      _.startCase(assetClass);
    const investmentChild = createMenuNode(
      { name: assetGroupName },
      investmentMenu
    );
    investmentChild.children = investmentByClass[assetClass].map((asset) =>
      assetToMenuNode(asset, investmentChild)
    );
    return investmentChild;
  });
  investmentMenu.children = investmentClasses || [];
  sortByAlphabet(investmentMenu.children);
}

function updateCashMenu(cashAssets) {
  const cashByType = _.groupBy(cashAssets, "asset_type");
  const cashs = Object.keys(cashByType).map((assetType) => {
    const cashChild = createMenuNode(
      {
        name: _.startCase(assetType),
      },
      cashMenu
    );
    cashChild.children = cashByType[assetType].map((asset) =>
      assetToMenuNode(asset, cashChild)
    );
    return cashChild;
  });
  const assetsWithCashType =
    cashs.find((e) => e.name == "Cash")?.children || [];
  assetsWithCashType.forEach((item) => {
    item.level = 1;
    item.parentId = cashMenu.id;
  });
  sortByAlphabet(assetsWithCashType);
  sortByAlphabet(cashs);
  cashMenu.children = [
    ...assetsWithCashType,
    ...cashs.filter((item) => item.name !== "Cash"),
  ];
}

function updateLoanMenu(loanAssets) {
  loanMenu.children = loanAssets.map((asset) =>
    assetToMenuNode(asset, loanMenu)
  );
  sortByAlphabet(loanMenu.children);
}

function getMenuByPathName(pathname) {
  return menuNodes.find((e) => e.path === pathname);
}

function onMenuChange(pathname) {
  // Reset activated
  menuNodes.forEach((node) => (node.activated = false));
  menuActivated = getMenuByPathName(pathname);
  relativeMenus = getRelativeMenus(menuActivated);
  if (menuActivated) {
    menuActivated.activated = true;
    // Expand all parent nodes
    relativeMenus.forEach((e) => e !== menuActivated && (e.expanded = true));
  }
}

function getRelativeMenus(menu, list = []) {
  if (!menu) {
    return list;
  }
  if (menu.parentId) {
    getRelativeMenus(
      menuNodes.find((e) => e.id === menu.parentId),
      list
    );
  }
  list.push(menu);
  return list;
}

export function getRelativeMenuActivated() {
  return relativeMenus.slice();
}

// Menu tree data
export const homeMenu = createMenuNode({
  name: "HOME",
  icon: <HomeIcon />,
  path: "/publicInstrument",
});
export const publicInstrument = createMenuNode({
  name: "Public Instrument",
  icon: <HomeIcon />,
  path: "/publicInstrument",
});

export const settingItem = createMenuNode({
  name: "Admin functions",
  icon: <SettingsIcon />,
  path: "/setting",
});
export const splitInstrumentMenuItem = createMenuNode({
  name: "Split for instrument",
  icon: <SettingsIcon />,
  path: "/splitinstrument",
});
export const ProviderErrorList = createMenuNode({
  name: "Provider Error List",
  icon: <ErrorOutlineOutlinedIcon />,
  path: "/providererrorlist",
});

export const connectionSessionMenuItem = createMenuNode({
  name: "Connection Session Data",
  icon: <Share />,
  path: "/connectionsessiondata",
});
export const holdingsMenu = createMenuNode({
  name: "HOLDINGS",
  path: "/holdings",
  className: menuStyles.title,
});
const investmentMenu = createMenuNode({
  name: "INVESTMENTS",
  icon: <AccountBalanceWalletIcon />,
  expanded: true,
  firstAction: "Add your first Investment",
  firstActionPath: "/addInvestment",
});

const cashMenu = createMenuNode({
  name: "CASH",
  icon: <LocalAtmIcon />,
  expanded: true,
  firstAction: "Add your first Cash Holding",
  firstActionPath: "/addCash",
});
const loanMenu = createMenuNode({
  name: "LOANS",
  icon: <AccountBalanceIcon />,
  expanded: true,
  firstAction: "Add your first Loan",
  firstActionPath: "/addLoan",
});
let assetMenu = [investmentMenu, cashMenu, loanMenu];

let menuChangedTimer;

export function useMenu() {
  const dispatch = useDispatch();
  const assetList = useSelector((state) => state?.asset?.assetList || []);
  const location = useLocation();
  const menuChanged = useCallback(() => {
    clearTimeout(menuChangedTimer);
    menuChangedTimer = setTimeout(
      () => dispatch(setMenuActivated(relativeMenus)),
      100
    );
  }, [dispatch]);

  const menu = useMemo(() => {
    const newMenu = buildMenuTree(assetList);
    menuChanged();
    return newMenu;
  }, [assetList, menuChanged]);

  useEffect(() => {
    onMenuChange(location.pathname);
    menuChanged();
  }, [location, menuChanged]);

  return menu;
}

export function useMenuStatus(menu) {
  const pathname = useSelector((state) => state.router.location.pathname);
  const [menuState, setMenuState] = useState({
    expanded: menu.expanded,
    activated: menu.activated,
    setExpanded: (expanded) => {
      menu.expanded = expanded;
      setMenuState((prev) => ({ ...prev, expanded: expanded }));
    },
  });

  useEffect(() => {
    // Sync menu status
    const timer = setTimeout(() => {
      // Don't have any changes
      if (
        menu.activated === menuState.activated &&
        menu.expanded === menuState.expanded
      ) {
        return;
      }
      setMenuState((prev) => ({
        ...prev,
        activated: menu.activated,
        expanded: menu.expanded,
      }));
    });
    return () => clearTimeout(timer);
  }, [menu, pathname, menuState]);

  return menuState;
}
